import { useState, useEffect, useRef, useContext } from "react";
import Receipt from "./Receipt";
import dayjs from "dayjs";
import useGetMerchantId from "../../../functions/Merchant/getMerchantId";
import useGetNewInvoiceNo from "../../../functions/Transaction/getNewInvoiceNo";
import { Button, Col, Flex, InputNumber, Row } from "antd";
import FunctionalButton from "../../Common/FunctionalButton";
import calculateChange from "../../../functions/Common/calculateChange";
import { useShowNotification } from "../../../functions/Common/showNotification";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { IndexContext } from "../../../context/IndexContext";

export default function Cash(props) {
  const {
    plates,
    onPaymentSuccessReset,
    receiptPrice,
    setReceiptPrice,
    transactionInfo,
    setTransactionInfo,
    setExtraChargesOptions,
    connectDevice,
    setConnectDevice,
  } = props;

  const [
    isDisableCompleteTransactionButton,
    setIsDisableCompleteTransactionButton,
  ] = useState(true);

  const [cashValue, setCashValue] = useState(0);
  const [paymentConfirm, setPaymentConfirm] = useState(false);

  const showNotification = useShowNotification();
  const { merchantId } = useGetMerchantId(receiptPrice);
  const { invoiceNo } = useGetNewInvoiceNo(receiptPrice);

  const confirmPayment = () => {
    if (cashValue < receiptPrice.total) {
      showNotification({
        message: "Insufficient Cash Payment",
        description:
          "The cash amount entered is less than the total amount. Please provide the full payment to proceed",
      });
    } else {
      setReceiptPrice((prev) => ({ ...prev, cash: cashValue }));
      setPaymentConfirm(true);
    }
  };

  const paymentReset = () => {
    setPaymentConfirm(false);
    onPaymentSuccessReset();
  };

  const handleStep = (value) => {
    setCashValue(value);
  };

  const handleKeyDown = (e) => {
    const charCode = e.keyCode || e.charCode;
    // Allow only numbers and decimal point
    if (
      (charCode < 48 || charCode > 57) && // Not a number (0-9)
      charCode !== 46 &&
      charCode !== 8 // Not a period "."
    ) {
      e.preventDefault();
    }
  };

  const handleKeyPress = (event) => {
    const invalidChars = /[!@#$%^&*()]/; // Regex to match invalid characters
    if (invalidChars.test(event.key)) {
      event.preventDefault(); // Prevent the character from being entered
    }
  };

  const handleNumpadInput = (value) => {
    let currentValue = cashValue !== null ? cashValue : 0; // Default to 0 if null

    if (typeof value === "number") {
      // Shift current value left (multiply by 10) and add new digit, then divide by 100 for cents
      let newValue = (currentValue * 10 + value / 100).toFixed(2);
      setCashValue(parseFloat(newValue));
    } else if (value === "back") {
      // Remove last digit by dividing by 10 and truncating to 2 decimal places
      let newValue = (Math.floor(currentValue * 10) / 100).toFixed(2);
      setCashValue(parseFloat(newValue));
    }
  };

  useEffect(() => {
    if (receiptPrice.cash != null) {
      let amount = calculateChange(receiptPrice);
      setReceiptPrice((prev) => ({
        ...prev,
        total: amount.total,
        change: amount.change,
        round_adj: amount.round_adj,
      }));
    }
  }, [receiptPrice?.cash]);

  useEffect(() => {
    if (invoiceNo && merchantId) {
      setIsDisableCompleteTransactionButton(false);
      setTransactionInfo({
        invoice_no: invoiceNo,
        payment_id: null,
        amount: receiptPrice.total,
        sst_amount: receiptPrice.sst,
        subtotal: receiptPrice.subtotal,
        transaction_datetime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        payment_method: "Cash",
        status: 1,
        merchant_id: merchantId,
        pg_transaction_id: null,
        signature: null,
        err_desc: null,
        pay_amount: receiptPrice.cash,
        change_amount: receiptPrice.change,
        round_adj_amount: receiptPrice.round_adj,
        takeaway_fee: receiptPrice.takeaway_fee,
      });
    }
  }, [invoiceNo, merchantId]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      {paymentConfirm ? (
        <Receipt
          plates={plates}
          onPaymentSuccessReset={paymentReset}
          transactionInfo={transactionInfo}
          setTransactionInfo={setTransactionInfo}
          isDisableCompleteTransactionButton={
            isDisableCompleteTransactionButton
          }
          setExtraChargesOptions={setExtraChargesOptions}
          connectDevice={connectDevice}
          setConnectDevice={setConnectDevice}
        />
      ) : (
        <Flex
          gap={"middle"}
          vertical="vertical"
          style={{ width: "100%", height: "100%" }}
        >
          <InputNumber
            className="cash-input-number"
            autoFocus={true}
            min={0}
            size="large"
            value={cashValue}
            step={0.01}
            onStep={handleStep}
            formatter={(value) =>
              `RM ${new Intl.NumberFormat("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(value || 0))}`
            }
            parser={(value) =>
              parseInt(value?.replace(/\RM\s?|[,.]/g, "")) / 100
            }
            onChange={(value) => setCashValue(value)}
            onKeyDown={handleKeyDown}
            onKeyPress={handleKeyPress}
            style={{ height: "30%" }}
          />
          <CustomNumPad handleNumpadInput={handleNumpadInput} />
          <Flex gap={"middle"} style={{ height: "20%" }}>
            <FunctionalButton
              width="30%"
              danger
              children={<strong style={{ fontSize: "24px" }}>Reset</strong>}
              buttonClickFunction={() => setCashValue(0)}
            />
            <FunctionalButton
              children={
                <strong style={{ fontSize: "24px" }}>Confirm Payment</strong>
              }
              width="70%"
              background="#38B865"
              buttonClickFunction={confirmPayment}
            />
          </Flex>
        </Flex>
      )}
    </div>
  );
}

function CustomNumPad({ handleNumpadInput }) {
  const { isLightTheme } = useContext(IndexContext);

  const NUMPAD_FORMAT = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    [null, 0, "back"],
  ];

  return (
    <Flex vertical="vertical" gap={"middle"} style={{ height: "100%" }}>
      {NUMPAD_FORMAT.map((row, rowIndex) => (
        <Row
          key={rowIndex}
          gutter={[12, 12]}
          align={"middle"}
          style={{ height: "25%" }}
        >
          {row.map((digit, colIndex) => (
            <Col
              key={colIndex}
              span={8}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              {digit != null && (
                <Button
                  style={{
                    width: "100%",
                    height: "100%",
                    fontSize: "40px",
                    fontWeight: "bold",
                    borderRadius: "8px",
                    // border: "1px solid #d9d9d9",
                    boxShadow: "0 2px 0 rgba(0, 0, 0, 0.02)",
                    background: isLightTheme ? "#E9EAEC" : "#606060",
                  }}
                  onClick={() => handleNumpadInput(digit)}
                >
                  {digit === "back" ? <ArrowLeftOutlined /> : digit}
                </Button>
              )}
            </Col>
          ))}
        </Row>
      ))}
    </Flex>
  );
}
